import { useEffect, useState, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '@/context/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { StyledSelect } from '@/components/ui/styled-select';
import {
  Check, Crown, Star, Zap, Loader2, Lock,
  Receipt, CreditCard, Download, XCircle, ChevronRight, Calendar, DollarSign
} from 'lucide-react';
import Nav from '@/components/Nav';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// UI-only decorations (CTA label, icon, popular flag, upgradeMode).
// All plan content (features, price, currency, labels) comes from backend plans_master.
const PLAN_UI = {
  free: { cta: 'Current Plan', icon: Star, upgradeMode: null },
  premium: { cta: 'Upgrade to Premium', icon: Crown, popular: true, upgradeMode: 'stripe' },
  elite: { cta: 'Request Invite', icon: Zap, upgradeMode: 'invite' },
};

const SubscriptionPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [currentTier, setCurrentTier] = useState('free');
  const [loading, setLoading] = useState(false);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [activeTab, setActiveTab] = useState('plans');
  const [billingHistory, setBillingHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [cancelConfirm, setCancelConfirm] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [eliteInviteOpen, setEliteInviteOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState({ phone: '', preferred_contact: 'email', message: '' });
  const [submittingInvite, setSubmittingInvite] = useState(false);
  const invoiceRef = useRef(null);

  const [plans, setPlans] = useState(null);  // merged plans from backend (region-aware)
  const [planRegion, setPlanRegion] = useState('UAE');

  const fetchPlans = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/subscription/plans`);
      setPlans(res.data.plans || {});
      setPlanRegion(res.data.region || 'UAE');
    } catch (error) {
      setPlans({});
    }
  }, []);

  const fetchSubscriptionStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/subscription/status`);
      setCurrentTier(res.data.tier);
    } catch (error) {
    }
  }, []);

  const fetchBillingHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const res = await axios.get(`${API}/subscription/billing-history`);
      setBillingHistory(res.data.transactions || []);
    } catch (error) {
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  const pollPaymentStatus = useCallback(async (sessionId, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 3000;
    if (attempts >= maxAttempts) {
      toast.error('Payment verification timed out. If you were charged, your plan will update shortly.');
      setCheckingPayment(false);
      navigate('/subscription', { replace: true });
      return;
    }
    setCheckingPayment(true);
    try {
      const res = await axios.get(`${API}/subscription/checkout/status/${sessionId}`);
      if (res.data.payment_status === 'paid' || res.data.status === 'completed') {
        toast.success('Payment successful! Your subscription has been upgraded.');
        fetchSubscriptionStatus();
        setCheckingPayment(false);
        setActiveTab('manage');
        navigate('/subscription', { replace: true });
        return;
      } else if (res.data.status === 'expired') {
        toast.error('Payment session expired. Please try again.');
        setCheckingPayment(false);
        navigate('/subscription', { replace: true });
        return;
      }
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      if (attempts >= 2) {
        toast.error('Error checking payment status. Your plan will update if payment succeeded.');
        setCheckingPayment(false);
        navigate('/subscription', { replace: true });
      } else {
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
      }
    }
  }, [navigate, fetchSubscriptionStatus]);

  useEffect(() => {
    fetchSubscriptionStatus();
    fetchPlans();
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      pollPaymentStatus(sessionId);
    }
  }, [searchParams, fetchSubscriptionStatus, fetchPlans, pollPaymentStatus]);

  useEffect(() => {
    if (activeTab === 'manage') fetchBillingHistory();
  }, [activeTab, fetchBillingHistory]);

  const handleUpgrade = async (plan) => {
    if (plan === 'free' || plan === currentTier) return;
    if (plan === 'elite') {
      setEliteInviteOpen(true);
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/subscription/checkout`, {
        plan,
        origin_url: window.location.origin
      });
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (error) {
      toast.error('Failed to start checkout. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestInvite = async () => {
    setSubmittingInvite(true);
    try {
      await axios.post(`${API}/subscription/request-invite`, inviteForm);
      toast.success('Invite request submitted! Our concierge will contact you within 24-48 hours.');
      setEliteInviteOpen(false);
      setInviteForm({ phone: '', preferred_contact: 'email', message: '' });
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to submit request. Please try again.';
      toast.error(msg);
    } finally {
      setSubmittingInvite(false);
    }
  };

  const handleCancel = async () => {
    setCancelling(true);
    try {
      await axios.post(`${API}/subscription/cancel`);
      toast.success('Subscription cancelled. You are now on the Free plan.');
      setCurrentTier('free');
      setCancelConfirm(false);
      fetchBillingHistory();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to cancel subscription.';
      toast.error(msg);
    } finally {
      setCancelling(false);
    }
  };

  const handlePrintInvoice = (tx) => {
    const w = window.open('', '_blank', 'width=700,height=900');
    if (!w) return;
    const planLabel = tx.plan?.charAt(0).toUpperCase() + tx.plan?.slice(1);
    const date = new Date(tx.created_at).toLocaleDateString('en-AE', { year: 'numeric', month: 'long', day: 'numeric' });
    const statusBadge = tx.payment_status === 'paid' ? 'Paid' : tx.payment_status === 'cancelled' ? 'Cancelled' : 'Pending';
    const statusRow = tx.payment_status === 'paid' ? 'Paid' : tx.payment_status === 'cancelled' ? 'Cancelled' : tx.status;

    const doc = w.document;
    doc.open();

    const head = doc.createElement('head');
    const title = doc.createElement('title');
    title.textContent = 'ASTIIV BASGAT Invoice';
    head.appendChild(title);
    const style = doc.createElement('style');
    style.textContent = `body{font-family:'Segoe UI',sans-serif;color:#1a1a1a;padding:48px;max-width:640px;margin:0 auto}.header{display:flex;justify-content:space-between;align-items:center;border-bottom:2px solid #C6A15B;padding-bottom:24px;margin-bottom:32px}.logo{font-size:28px;font-weight:700;color:#C6A15B;letter-spacing:1px}.badge{background:#C6A15B;color:#000;padding:4px 14px;border-radius:20px;font-size:12px;font-weight:600;text-transform:uppercase}.row{display:flex;justify-content:space-between;padding:12px 0;border-bottom:1px solid #eee}.label{color:#666;font-size:14px}.value{font-weight:600;font-size:14px}.total{font-size:22px;font-weight:700;color:#C6A15B;text-align:right;margin-top:24px}.footer{margin-top:48px;font-size:12px;color:#999;text-align:center;border-top:1px solid #eee;padding-top:16px}@media print{body{padding:24px}}`;
    head.appendChild(style);
    doc.documentElement.appendChild(head);

    const body = doc.createElement('body');
    body.innerHTML = [
      `<div class="header"><div class="logo">ASTIIV BASGAT</div><div class="badge">${doc.createTextNode(statusBadge).textContent}</div></div>`,
      `<h2 style="margin-bottom:24px;font-weight:400">Invoice</h2>`,
      `<div class="row"><span class="label">Transaction ID</span><span class="value">${doc.createTextNode(tx.id || tx.session_id).textContent}</span></div>`,
      `<div class="row"><span class="label">Date</span><span class="value">${doc.createTextNode(date).textContent}</span></div>`,
      `<div class="row"><span class="label">Plan</span><span class="value">ASTIIV BASGAT ${doc.createTextNode(planLabel).textContent}</span></div>`,
      `<div class="row"><span class="label">Amount</span><span class="value">AED ${doc.createTextNode(Number(tx.amount).toLocaleString()).textContent}</span></div>`,
      `<div class="row"><span class="label">Currency</span><span class="value">${doc.createTextNode((tx.currency || 'AED').toUpperCase()).textContent}</span></div>`,
      `<div class="row"><span class="label">Status</span><span class="value">${doc.createTextNode(statusRow).textContent}</span></div>`,
      `<div class="total">Total: AED ${doc.createTextNode(Number(tx.amount).toLocaleString()).textContent}</div>`,
      `<div class="footer">ASTIIV BASGAT — AI-Powered Lifestyle Portfolio Intelligence<br/>This is an auto-generated invoice for your records.</div>`,
    ].join('');
    doc.documentElement.appendChild(body);
    doc.close();
    w.onload = () => w.print();
  };

  const effectiveTierForDisplay = currentTier === 'admin' ? 'elite' : currentTier;
  const currentPlanFromApi = (plans && plans[effectiveTierForDisplay]) || (plans && plans.free) || null;
  const currentPlanUI = PLAN_UI[effectiveTierForDisplay] || PLAN_UI.free;
  const tierLabel = currentTier === 'admin' ? 'Admin (Full Access)' : (currentPlanFromApi?.labels?.name || 'Free');
  const TierIcon = currentPlanUI.icon;
  // Unified plan-data view used by the Manage tab UI — pulls price + features
  // from the API (regionalised pricing) with a defensive fallback so the page
  // never crashes if `/api/subscription/plans` is slow or returns a partial.
  const currentPlanData = {
    name: tierLabel,
    price: currentPlanFromApi?.price ?? 0,
    currency: currentPlanFromApi?.currency || (planRegion === 'IN' ? 'INR' : 'AED'),
    features: currentPlanFromApi?.features || currentPlanUI.features || [],
  };

  // Helpers to replace nested ternaries
  const iconColor = (key) => {
    if (key === 'elite') return 'text-yellow-400';
    if (key === 'premium') return 'text-gold';
    return 'text-text-secondary';
  };

  const buttonClass = (isCurrentPlan, popular, planKey) => {
    if (isCurrentPlan) return 'bg-white/10 text-text-secondary cursor-default';
    if (planKey === 'elite') return '!bg-gradient-to-r !from-yellow-400 !via-amber-400 !to-[#D4AF37] !text-black hover:!from-yellow-300 hover:!via-amber-300 hover:!to-[#F1D693] shadow-lg shadow-yellow-500/30 font-semibold';
    if (popular) return 'bg-gold text-black hover:bg-gold-light';
    return 'bg-white/10 hover:bg-white/20';
  };

  const buttonLabel = (isCurrentPlan, isUpgrade, cta) => {
    if (loading) return <Loader2 className="w-4 h-4 animate-spin" />;
    if (isCurrentPlan) return 'Current Plan';
    if (isUpgrade) return cta;
    return 'Included';
  };

  const txStatusColor = (isPaid, isCancellation) => {
    if (isPaid) return 'text-emerald-400';
    if (isCancellation) return 'text-red-400';
    return 'text-yellow-400';
  };

  const txBgColor = (isPaid, isCancellation) => {
    if (isCancellation) return 'bg-red-500/10';
    if (isPaid) return 'bg-emerald-500/10';
    return 'bg-yellow-500/10';
  };

  const txStatusLabel = (isPaid, isCancellation, status) => {
    if (isPaid) return 'Paid';
    if (isCancellation) return 'Cancelled';
    return status;
  };

  return (
    <div className="min-h-screen bg-obsidian text-text-primary mobile-pb">
      <Nav showBack />

      <div className="max-w-[1440px] mx-auto px-4 sm:px-8 py-12 sm:py-16">
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-10">
          <h1 className="font-playfair text-4xl sm:text-5xl font-semibold mb-3">Your Subscription</h1>
          <p className="text-text-secondary text-base sm:text-lg max-w-2xl mx-auto">
            Manage your plan, view billing history, and download invoices
          </p>
        </motion.div>

        {/* Tabs */}
        <div className="flex justify-center gap-2 mb-10" data-testid="subscription-tabs">
          {[
            { key: 'plans', label: 'Plans', icon: CreditCard },
            { key: 'manage', label: 'Manage', icon: Receipt }
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              data-testid={`tab-${tab.key}`}
              className={`flex items-center gap-2 px-6 py-2.5 rounded-full text-sm font-medium transition-all duration-200 ${
                activeTab === tab.key
                  ? 'bg-gold text-black'
                  : 'bg-white/5 text-text-secondary hover:bg-white/10'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Checking Payment */}
        {checkingPayment && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="glass-card p-8 text-center mb-8">
            <Loader2 className="w-8 h-8 text-gold mx-auto mb-4 animate-spin" />
            <p className="text-lg">Verifying your payment...</p>
          </motion.div>
        )}

        <AnimatePresence mode="wait">
          {activeTab === 'plans' && (
            <motion.div key="plans" initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }} transition={{ duration: 0.25 }}>
              {/* Pricing Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-testid="plans-grid">
                {Object.entries(plans || {}).map(([key, plan], index) => {
                  const ui = PLAN_UI[key] || {};
                  const Icon = ui.icon || Star;
                  const effKey = (plans && plans[currentTier]) ? currentTier : (currentTier === 'admin' ? 'elite' : 'free');
                  const isCurrentPlan = key === currentTier || (currentTier === 'admin' && key === 'elite');
                  const isUpgrade = plan.price > ((plans && plans[effKey]?.price) || 0);
                  const currencySymbol = plan.currency === 'INR' ? '₹' : (plan.currency || 'AED') + ' ';
                  const priceDisplay = plan.currency === 'INR'
                    ? `₹${Number(plan.price).toLocaleString('en-IN')}`
                    : `${plan.currency || 'AED'} ${Number(plan.price).toLocaleString()}`;
                  return (
                    <motion.div
                      key={key}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className={`glass-card p-8 relative ${ui.popular ? 'border-2 border-gold' : ''} ${isCurrentPlan ? 'ring-2 ring-gold/50' : ''}`}
                      data-testid={`plan-card-${key}`}
                    >
                      {ui.popular && (
                        <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-gold text-black px-4 py-1 rounded-full text-sm font-bold">
                          Most Popular
                        </div>
                      )}
                      <div className="text-center mb-8">
                        <Icon className={`w-12 h-12 mx-auto mb-4 ${iconColor(key)}`} />
                        <h3 className="font-playfair text-2xl font-semibold mb-2">{plan.labels?.name || key}</h3>
                        <div className="flex items-baseline justify-center gap-1">
                          <span className="text-4xl font-bold text-gold" data-testid={`plan-price-${key}`}>
                            {plan.price > 0 ? priceDisplay : `${plan.currency || 'AED'} 0`}
                          </span>
                          {plan.price > 0 && <span className="text-text-secondary">/month</span>}
                        </div>
                      </div>
                      <ul className="space-y-4 mb-8">
                        {(plan.features || []).map((f, i) => (
                          <li key={i} className="flex items-start gap-3">
                            <Check className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
                            <span className="text-text-secondary">{f}</span>
                          </li>
                        ))}
                        {(plan.locked_features || []).map((f, i) => (
                          <li key={`l-${i}`} className="flex items-start gap-3">
                            <Lock className="w-4 h-4 text-white/15 flex-shrink-0 mt-0.5" />
                            <span className="text-white/20">{f}</span>
                          </li>
                        ))}
                      </ul>
                      <Button
                        onClick={() => handleUpgrade(key)}
                        disabled={loading || isCurrentPlan || !isUpgrade || checkingPayment}
                        className={`w-full ${buttonClass(isCurrentPlan, ui.popular, key)}`}
                        data-testid={`select-plan-${key}`}
                      >
                        {buttonLabel(isCurrentPlan, isUpgrade, ui.cta)}
                      </Button>
                    </motion.div>
                  );
                })}
                {(!plans || Object.keys(plans).length === 0) && (
                  <div className="md:col-span-3 glass-card p-12 text-center text-text-secondary">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto mb-4 text-gold" />
                    Loading plans…
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {activeTab === 'manage' && (
            <motion.div key="manage" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.25 }}>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Current Plan Card */}
                <div className="lg:col-span-1">
                  <div className="glass-card p-6 relative overflow-hidden" data-testid="current-plan-card">
                    <div className="absolute top-0 right-0 w-32 h-32 bg-gold/5 rounded-full -translate-y-1/2 translate-x-1/2" />
                    <h3 className="text-sm font-medium text-text-secondary uppercase tracking-wider mb-4">Current Plan</h3>
                    <div className="flex items-center gap-3 mb-4">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                        effectiveTierForDisplay === 'elite' ? 'bg-yellow-400/10' :
                        effectiveTierForDisplay === 'premium' ? 'bg-gold/10' : 'bg-white/5'
                      }`}>
                        <TierIcon className={`w-6 h-6 ${
                          effectiveTierForDisplay === 'elite' ? 'text-yellow-400' :
                          effectiveTierForDisplay === 'premium' ? 'text-gold' : 'text-text-secondary'
                        }`} />
                      </div>
                      <div>
                        <p className="text-xl font-semibold">{tierLabel}</p>
                        <p className="text-gold text-sm">
                          {currentPlanData.currency} {currentPlanData.price.toLocaleString()}{currentPlanData.price > 0 ? '/month' : ''}
                        </p>
                      </div>
                    </div>

                    {currentTier !== 'free' && currentTier !== 'admin' && (
                      <>
                        <div className="border-t border-white/5 pt-4 mt-4">
                          <Button
                            variant="outline"
                            onClick={() => setActiveTab('plans')}
                            className="w-full border-gold/30 text-gold hover:bg-gold/10 mb-3"
                            data-testid="change-plan-btn"
                          >
                            <ChevronRight className="w-4 h-4 mr-2" /> Change Plan
                          </Button>
                        </div>
                      </>
                    )}

                    {currentTier === 'free' && (
                      <div className="border-t border-white/5 pt-4 mt-4">
                        <Button
                          onClick={() => setActiveTab('plans')}
                          className="w-full bg-gold text-black hover:bg-gold-light"
                          data-testid="upgrade-from-manage-btn"
                        >
                          Upgrade Your Plan
                        </Button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Billing History */}
                <div className="lg:col-span-2">
                  <div className="glass-card p-6" data-testid="billing-history-card">
                    <div className="flex items-center justify-between mb-6">
                      <h3 className="text-sm font-medium text-text-secondary uppercase tracking-wider">Billing History</h3>
                      <span className="text-xs text-text-secondary">{billingHistory.length} transaction{billingHistory.length !== 1 ? 's' : ''}</span>
                    </div>

                    {loadingHistory ? (
                      <div className="flex items-center justify-center py-12">
                        <Loader2 className="w-6 h-6 text-gold animate-spin" />
                      </div>
                    ) : billingHistory.length === 0 ? (
                      <div className="text-center py-12">
                        <Receipt className="w-10 h-10 text-white/10 mx-auto mb-3" />
                        <p className="text-text-secondary text-sm">No transactions yet</p>
                        <p className="text-white/30 text-xs mt-1">Transactions will appear here after your first payment</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {billingHistory.map((tx, i) => {
                          const planLabel = tx.plan?.charAt(0).toUpperCase() + tx.plan?.slice(1);
                          const isCancellation = tx.payment_status === 'cancelled';
                          const isPaid = tx.payment_status === 'paid';
                          const date = new Date(tx.created_at).toLocaleDateString('en-AE', { year: 'numeric', month: 'short', day: 'numeric' });
                          return (
                            <motion.div
                              key={tx.id || i}
                              initial={{ opacity: 0, y: 8 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: i * 0.04 }}
                              className="flex items-center justify-between p-4 rounded-lg bg-white/[0.02] hover:bg-white/[0.04] transition-colors group"
                              data-testid={`billing-row-${i}`}
                            >
                              <div className="flex items-center gap-4">
                                <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${txBgColor(isPaid, isCancellation)}`}>
                                  {isCancellation ? (
                                    <XCircle className="w-4 h-4 text-red-400" />
                                  ) : isPaid ? (
                                    <Check className="w-4 h-4 text-emerald-400" />
                                  ) : (
                                    <Calendar className="w-4 h-4 text-yellow-400" />
                                  )}
                                </div>
                                <div>
                                  <p className="text-sm font-medium">
                                    {isCancellation ? `Cancelled ${tx.metadata?.previous_tier || ''} plan` : `ASTIIV BASGAT ${planLabel}`}
                                  </p>
                                  <p className="text-xs text-text-secondary">{date}</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-4">
                                <div className="text-right">
                                  <p className="text-sm font-semibold">
                                    {isCancellation ? '--' : `AED ${Number(tx.amount).toLocaleString()}`}
                                  </p>
                                  <p className={`text-xs ${txStatusColor(isPaid, isCancellation)}`}>
                                    {txStatusLabel(isPaid, isCancellation, tx.status)}
                                  </p>
                                </div>
                                {!isCancellation && (
                                  <button
                                    onClick={() => handlePrintInvoice(tx)}
                                    className="p-2 rounded-lg bg-white/0 hover:bg-white/5 transition-colors opacity-0 group-hover:opacity-100"
                                    title="Download Invoice"
                                    data-testid={`download-invoice-${i}`}
                                  >
                                    <Download className="w-4 h-4 text-text-secondary" />
                                  </button>
                                )}
                              </div>
                            </motion.div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Elite Invite Modal */}
        <AnimatePresence>
          {eliteInviteOpen && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
              onClick={() => !submittingInvite && setEliteInviteOpen(false)}
            >
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                className="glass-card p-8 max-w-md w-full border border-yellow-500/30"
                onClick={e => e.stopPropagation()}
                data-testid="elite-invite-modal"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-yellow-400/10 flex items-center justify-center">
                    <Zap className="w-6 h-6 text-yellow-400" />
                  </div>
                  <div>
                    <h3 className="font-playfair text-2xl font-semibold">Request Elite Invitation</h3>
                    <p className="text-text-secondary text-xs">Private, invite-only membership</p>
                  </div>
                </div>
                <p className="text-text-secondary text-sm mb-6">
                  Elite is reserved for our most discerning members. Our concierge will personally reach out within 24&ndash;48 hours to discuss your membership.
                </p>
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs uppercase tracking-wider text-text-secondary mb-2">Phone (optional)</label>
                    <input
                      type="tel"
                      value={inviteForm.phone}
                      onChange={e => setInviteForm({ ...inviteForm, phone: e.target.value })}
                      className="w-full px-4 py-2.5 rounded-lg bg-white/5 border border-white/10 focus:border-yellow-500/50 outline-none text-sm"
                      placeholder="+971 XX XXX XXXX"
                      data-testid="elite-invite-phone"
                    />
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-wider text-text-secondary mb-2">Preferred Contact</label>
                    <StyledSelect
                      value={inviteForm.preferred_contact}
                      onChange={(v) => setInviteForm({ ...inviteForm, preferred_contact: v })}
                      options={[{value:'email',label:'Email'},{value:'phone',label:'Phone'},{value:'whatsapp',label:'WhatsApp'}]}
                      data-testid="elite-invite-contact"
                    />
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-wider text-text-secondary mb-2">Message (optional)</label>
                    <textarea
                      rows={3}
                      value={inviteForm.message}
                      onChange={e => setInviteForm({ ...inviteForm, message: e.target.value })}
                      className="w-full px-4 py-2.5 rounded-lg bg-white/5 border border-white/10 focus:border-yellow-500/50 outline-none text-sm resize-none"
                      placeholder="Tell our concierge about your lifestyle goals"
                      data-testid="elite-invite-message"
                    />
                  </div>
                </div>
                <div className="flex gap-3 mt-6">
                  <Button
                    variant="outline"
                    onClick={() => setEliteInviteOpen(false)}
                    disabled={submittingInvite}
                    className="flex-1 border-white/20"
                    data-testid="elite-invite-cancel"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleRequestInvite}
                    disabled={submittingInvite}
                    className="flex-1 bg-gradient-to-r from-yellow-400 via-amber-400 to-[#D4AF37] text-black hover:from-yellow-300 hover:via-amber-300 hover:to-[#F1D693] font-semibold"
                    data-testid="elite-invite-submit"
                  >
                    {submittingInvite ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Submit Request'}
                  </Button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Cancel Confirmation Modal */}
        <AnimatePresence>
          {cancelConfirm && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
              onClick={() => !cancelling && setCancelConfirm(false)}
            >
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                className="glass-card p-8 max-w-md w-full"
                onClick={e => e.stopPropagation()}
                data-testid="cancel-confirm-modal"
              >
                <XCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
                <h3 className="font-playfair text-2xl font-semibold text-center mb-2">Cancel Subscription?</h3>
                <p className="text-text-secondary text-center text-sm mb-6">
                  You will immediately lose access to {currentPlanData.name} features and be downgraded to the Free plan.
                </p>
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => setCancelConfirm(false)}
                    disabled={cancelling}
                    className="flex-1 border-white/20"
                    data-testid="cancel-keep-plan-btn"
                  >
                    Keep My Plan
                  </Button>
                  <Button
                    onClick={handleCancel}
                    disabled={cancelling}
                    className="flex-1 bg-red-500 hover:bg-red-600 text-white"
                    data-testid="cancel-confirm-btn"
                  >
                    {cancelling ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Yes, Cancel'}
                  </Button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* FAQ */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }} className="mt-16 text-center">
          <h3 className="font-playfair text-2xl font-semibold mb-4">Questions?</h3>
          <p className="text-text-secondary mb-4">Contact our concierge for personalized assistance</p>
          <Button variant="outline" onClick={() => navigate('/concierge')} className="border-white/20 hover:border-gold">
            Talk to Concierge
          </Button>
        </motion.div>
      </div>
    </div>
  );
};

export default SubscriptionPage;
